import { Injectable } from '@angular/core';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { ResellerItem } from '@vendasta/store';
import { combineLatest, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { AnalyticsService } from '../../../analytics';
import { PurchaseCost } from '../../../core/billing/purchase-cost';
import { resellerArrayToMap } from '../../../core/reseller';
import { ResellerItemService } from '../../../core/reseller/reseller-item.service';
import { ActivationResult } from '../activate-app/interface';
import { ActivationParamService } from '../activation-param.service';
import { ActivationPricingService } from '../activation-pricing.service';
import {
  buildTransaction,
  convertActivationResultsToQuantityMap,
  convertResellerItemsAndPricingMapToAnalyticsItems,
  filterMapByIds,
  uuidv4,
} from './utils';

@Injectable()
export class ActivationAnalyticsService {
  // this is a map of sku to purchase cost
  private purchaseCostMap$: Observable<Map<string, PurchaseCost>>;
  private resellerItemMap$: Observable<Map<string, ResellerItem>>;
  private resellerItems$: Observable<ResellerItem[]>;
  private partnerId$: Observable<string>;
  private marketId$: Observable<string>;

  constructor(
    private resellerItemService: ResellerItemService,
    private activationPricingService: ActivationPricingService,
    private analyticsService: AnalyticsService,
    private activationParamService: ActivationParamService,
    private productAnalytics: ProductAnalyticsService,
  ) {
    this.resellerItems$ = this.resellerItemService.getActivatablePartnerData();
    this.resellerItemMap$ = this.resellerItems$.pipe(map((ris) => resellerArrayToMap(ris)));
    this.partnerId$ = this.activationParamService.partnerId$;
    this.marketId$ = this.activationParamService.marketId$;
  }

  trackActivationTransactions(activationResults: ActivationResult[]): Observable<void> {
    const randomTransactionId$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      map(([partnerId, marketId]) => {
        const uniqueTransactionId = uuidv4();
        return `${partnerId}-${marketId}-${uniqueTransactionId}`;
      }),
    );
    const analyticsItems = combineLatest([this.purchaseCostMap$, this.resellerItemMap$]).pipe(
      first(),
      map(([purchaseCostMap, resellerItemMap]) => {
        const activatedItemIds = activationResults.map((r) => r.appId);
        const activatedResellerMap = filterMapByIds(activatedItemIds, resellerItemMap);
        const activatedItemMap = convertActivationResultsToQuantityMap(activationResults);
        return convertResellerItemsAndPricingMapToAnalyticsItems(
          purchaseCostMap,
          activatedResellerMap,
          activatedItemMap,
        );
      }),
    );

    const transaction = combineLatest([this.partnerId$, this.marketId$, analyticsItems, randomTransactionId$]).pipe(
      first(),
      map(([partnerId, marketId, analyticsItemsInput, transactionId]) => {
        return buildTransaction(transactionId, partnerId, marketId, analyticsItemsInput);
      }),
    );

    return combineLatest([transaction, analyticsItems]).pipe(
      first(),
      map(([analyticsTransaction, analyticsItemsInput]) => {
        this.analyticsService.trackEcommerceTransaction(analyticsTransaction, analyticsItemsInput);
      }),
    );
  }
}
